import { createRouter, createWebHistory } from "vue-router";
import { ElMessage } from "element-plus";
import { useUserStore } from "@/store/modules/user";

const routes = [
  {
    path: "/login",
    name: "Login",
    component: () => import("@/views/login/Login.vue"),
    meta: { title: "登录", keepAlive: false, noAuth: true },
  },
  {
    path: "/",
    component: () => import("@/layouts/MainLayout.vue"),
    children: [
      {
        path: "",
        name: "Dashboard",
        component: () => import("@/views/dashboard/Index.vue"),
        meta: {
          title: "概览",
          eyebrow: "工作台",
          icon: "HomeFilled",
          keepAlive: false,
        },
      },
      {
        path: "lease/order",
        name: "Order",
        component: () => import("@/views/lease/Order.vue"),
        meta: {
          title: "订单工作台",
          eyebrow: "订单管理",
          icon: "Document",
          keepAlive: true,
        },
      },
      {
        path: "device",
        name: "Device",
        component: () => import("@/views/device/Index.vue"),
        meta: {
          title: "设备型号",
          eyebrow: "设备管理",
          icon: "Goods",
          keepAlive: true,
        },
      },
      {
        path: "device/type/:id",
        name: "TypeDetail",
        component: () => import("@/views/device/TypeDetail.vue"),
        meta: {
          title: "型号详情",
          eyebrow: "设备管理",
          parent: "/device",
          keepAlive: false,
        },
      },
      {
        path: "device/import",
        name: "BatchImport",
        component: () => import("@/views/device/BatchImport.vue"),
        meta: {
          title: "批量导入",
          eyebrow: "设备管理",
          parent: "/device",
          keepAlive: false,
        },
      },
      {
        path: "device/code-print",
        name: "DeviceCodePrint",
        component: () => import("@/views/device/DeviceCodePrint.vue"),
        meta: {
          title: "设备识别码打印",
          eyebrow: "设备管理",
          parent: "/device",
          keepAlive: false,
        },
      },
      {
        path: "lease/schedule",
        name: "Schedule",
        component: () => import("@/views/lease/Schedule.vue"),
        meta: {
          title: "设备档期表",
          eyebrow: "档期管理",
          icon: "Calendar",
          keepAlive: false,
        },
      },
      {
        path: "finance",
        name: "Finance",
        component: () => import("@/views/finance/Index.vue"),
        meta: { title: "收支结算", eyebrow: "财务管理", keepAlive: true },
      },
      {
        path: "config",
        name: "Config",
        component: () => import("@/views/config/Index.vue"),
        meta: { title: "闲管家同步配置", eyebrow: "业务配置", keepAlive: true },
      },
      {
        path: "customers",
        name: "Customers",
        component: () => import("@/views/customers/Index.vue"),
        meta: { title: "客户档案", eyebrow: "客户管理", keepAlive: true },
      },
      {
        path: "members",
        name: "Members",
        component: () => import("@/views/members/Index.vue"),
        meta: { title: "成员管理", eyebrow: "账号管理", keepAlive: true },
      },
      {
        path: "settings",
        name: "Settings",
        component: () => import("@/views/settings/Index.vue"),
        meta: { title: "设置", eyebrow: "店铺管理", keepAlive: true },
      },
    ],
  },
  {
    path: "/:pathMatch(.*)*",
    name: "NotFound",
    component: () => import("@/views/error/NotFound.vue"),
    meta: { keepAlive: false },
  },
];

// 懒加载路由注册表：记录「绝对路径 → import() 函数」，供菜单 hover / 空闲时预取 chunk
const routeLoaders = new Map();

const collectRouteLoaders = (list, parentPath = "") => {
  list.forEach((item) => {
    const fullPath = item.path.startsWith("/")
      ? item.path
      : `${parentPath}/${item.path}`.replace(/\/+/g, "/");
    if (typeof item.component === "function")
      routeLoaders.set(fullPath, item.component);
    if (item.children) collectRouteLoaders(item.children, fullPath);
  });
};

collectRouteLoaders(routes);

/**
 * 预取指定路径对应的懒加载 chunk（菜单 hover 等意图明确的场景用）
 * @param {string} path 路由绝对路径，如 /lease/order
 * @returns {Promise<unknown>} 预取完成（失败静默，不阻塞交互）
 */
export const prefetchRoute = (path) => {
  const loader = routeLoaders.get(path);
  if (!loader) return Promise.resolve();
  return loader().catch(() => {});
};

/**
 * 逐条预取全部懒加载 chunk（应用空闲时用），避免首次切换页面等待下载
 * @returns {Promise<unknown>} 全部预取完成
 */
export const prefetchAllRoutes = () =>
  [...routeLoaders.values()].reduce(
    (chain, loader) => chain.then(() => loader().catch(() => {})),
    Promise.resolve(),
  );

const router = createRouter({
  history: createWebHistory(),
  routes,
});

// 免登录白名单（无需 token 即可访问的路径）
const whiteList = ["/login"];

// 全局前置守卫：设置页面标题 + 校验登录态
// - 已登录访问 /login → 跳概览页
// - 未登录访问受保护页面 → 跳登录页并记录来源
router.beforeEach((to, from, next) => {
  const userStore = useUserStore();
  //document.title = to.meta.title ? `${to.meta.title} - Lease Admin` : 'Lease Admin'

  if (userStore.token) {
    // token 已过期且无 refresh_token 可续期：清空会话视为未登录
    if (
      userStore.expiresAt &&
      Date.now() > userStore.expiresAt &&
      !userStore.refreshToken
    ) {
      userStore.resetState();
      next("/login");
      return;
    }
    if (to.path === "/login") {
      next("/");
    } else {
      next();
    }
  } else {
    if (whiteList.includes(to.path)) {
      next();
    } else {
      ElMessage.warning("请先登录");
      next(`/login?redirect=${to.path}`);
    }
  }
});

export default router;
